import { RedirectLoginOptions, useAuth0, User as Auth0User } from '@auth0/auth0-react'
import type { Profile } from '@doneverse/data/api'
import { getErrorStatus, isAuthHttpError, setAccessTokenGetter } from '@doneverse/data/api'
import { queryKeys, userProfileOptions } from '@doneverse/data/queries'
import { profileService } from '@doneverse/data/services'
import type { BusinessBriefData } from '@doneverse/shared'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { createStatusError } from '@/lib/auth-session'
import { betterAuthEnabled } from '@/lib/better-auth-config'
import {
  clearBetterAuthToken,
  fetchBetterAuthSession,
  getStoredBetterAuthToken,
  isBetterAuthUnavailableError,
  loginWithBetterAuthPassword,
  logoutBetterAuth,
  type BetterAuthSessionUser,
} from '@/lib/better-auth-runtime'
import { clearErrorUser, setErrorUser, setImpersonatedErrorSession } from '@/lib/error-user'
import { getImpersonatedUser, setImpersonatedUser } from '@/lib/impersonation'
import {
  isMobileBridge,
  postMobileBridgeSignOut,
  requestMobileBridgeRefresh,
  setMobileBridgeAcceptHandler,
} from '@/lib/mobile-bridge'
import { identifyUser, resetUser } from '@/lib/posthog'
import { bootstrapWorkspaceSelection } from '@/lib/workspace-bootstrap'
import { useDashboardStore } from '@/store'
export type { Profile } from '@doneverse/data/api'

export type AuthUser = Auth0User & { id: string }
type LoginResult = { error: Error | null }
type LogoutOptions = {
  returnTo?: string
}

interface DirectAuthSession {
  accessToken: string
  idToken?: string
  refreshToken?: string
  expiresAt: number
  user: AuthUser
}

/**
 * A live Better Auth session. Unlike the Auth0 sessions above there is no
 * refresh token to manage: the opaque bearer token is stable and the server
 * extends the session's expiry in place as it is used.
 */
interface BetterAuthSessionState {
  token: string
  user: AuthUser
}

/**
 * Better Auth session user -> the `AuthUser` shape the rest of the app reads.
 *
 * `id` is the canonical app user id (`users.id`) so every downstream consumer —
 * the profile query key, PostHog identity, the widget — keys off the same id it
 * does on the Auth0 paths. `sub` carries the Better Auth principal, since there
 * is no Auth0 `sub` in the browser on this path.
 */
function betterAuthUserToAuthUser(user: BetterAuthSessionUser): AuthUser {
  return {
    id: user.appUserId ?? user.id,
    sub: user.id,
    email: user.email || undefined,
    name: user.name || undefined,
    picture: user.image ?? undefined,
    email_verified: user.emailVerified,
  }
}

const DIRECT_AUTH_STORAGE_KEY = 'doneverse.direct_auth'
const TOKEN_REFRESH_BUFFER_MS = 30_000
const BETTER_AUTH_BOOT_RETRY_INITIAL_MS = 1_000
const BETTER_AUTH_BOOT_RETRY_MAX_MS = 10_000

function parseJwtPayload(token: string): Record<string, unknown> {
  const [, payload = ''] = token.split('.')
  const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=')
  const decoded = atob(padded)
  return JSON.parse(decoded) as Record<string, unknown>
}

function buildUserFromIdToken(idToken: string): AuthUser {
  const payload = parseJwtPayload(idToken)
  const sub = typeof payload.sub === 'string' ? payload.sub : ''
  if (!sub) {
    throw new Error('Invalid Auth0 ID token')
  }
  return {
    id: sub,
    sub,
    email: typeof payload.email === 'string' ? payload.email : undefined,
    name: typeof payload.name === 'string' ? payload.name : undefined,
    picture: typeof payload.picture === 'string' ? payload.picture : undefined,
    email_verified: typeof payload.email_verified === 'boolean' ? payload.email_verified : undefined,
    updated_at: typeof payload.updated_at === 'string' ? payload.updated_at : undefined,
  }
}

function readDirectSession(): DirectAuthSession | null {
  try {
    const raw = window.localStorage.getItem(DIRECT_AUTH_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<DirectAuthSession>
    if (
      typeof parsed.accessToken !== 'string' ||
      typeof parsed.expiresAt !== 'number' ||
      !parsed.user ||
      typeof parsed.user.sub !== 'string'
    ) {
      window.localStorage.removeItem(DIRECT_AUTH_STORAGE_KEY)
      return null
    }
    return {
      accessToken: parsed.accessToken,
      idToken: parsed.idToken,
      refreshToken: parsed.refreshToken,
      expiresAt: parsed.expiresAt,
      user: parsed.user,
    }
  } catch {
    window.localStorage.removeItem(DIRECT_AUTH_STORAGE_KEY)
    return null
  }
}

function writeDirectSession(session: DirectAuthSession | null) {
  if (!session) {
    window.localStorage.removeItem(DIRECT_AUTH_STORAGE_KEY)
    return
  }
  window.localStorage.setItem(DIRECT_AUTH_STORAGE_KEY, JSON.stringify(session))
}

function authErrorMessage(payload: unknown, fallback = 'Login failed. Please try again.') {
  if (!payload || typeof payload !== 'object') return fallback
  const data = payload as Record<string, unknown>
  const fromDescription = data.error_description
  if (typeof fromDescription === 'string' && fromDescription.trim()) return fromDescription
  const fromDescriptionAlt = data.description
  if (typeof fromDescriptionAlt === 'string' && fromDescriptionAlt.trim()) return fromDescriptionAlt
  const fromMessage = data.message
  if (typeof fromMessage === 'string' && fromMessage.trim()) return fromMessage
  const fromError = data.error
  if (typeof fromError === 'string' && fromError.trim()) return fromError
  return fallback
}

function resolveLogoutReturnTo(returnTo?: string): string {
  if (!returnTo) return window.location.origin
  if (returnTo.startsWith('/')) return `${window.location.origin}${returnTo}`
  return window.location.origin
}

export interface AuthContextType {
  user: AuthUser | undefined
  isAuthenticated: boolean
  isLoading: boolean
  loading: boolean // Alias for isLoading to maintain compatibility
  isLoggingOut: boolean
  profile: Profile | null
  authError: string | null // 'session_expired' when refresh token is missing
  authErrorStatus: number | null
  getAccessToken: () => Promise<string>
  getRealtimeJwt: () => Promise<string>
  loginWithPassword: (email: string, password: string) => Promise<LoginResult>
  signupWithPassword: (email: string, password: string) => Promise<LoginResult>
  loginWithRedirect: (options?: RedirectLoginOptions) => Promise<void>
  logout: (options?: LogoutOptions) => Promise<void>
  updateProfile: (
    updates: Partial<Omit<Profile, 'business_brief'>> & {
      business_brief?: BusinessBriefData
    },
  ) => Promise<{ error: Error | null }>
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const {
    user: auth0User,
    isAuthenticated: auth0IsAuthenticated,
    isLoading: auth0Loading,
    getAccessTokenSilently,
    getIdTokenClaims,
    loginWithRedirect: auth0LoginWithRedirect,
    logout: auth0Logout,
  } = useAuth0()

  const queryClient = useQueryClient()
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const [directSession, setDirectSession] = useState<DirectAuthSession | null>(() => readDirectSession())
  const [betterAuthSession, setBetterAuthSession] = useState<BetterAuthSessionState | null>(null)
  // The dashboard flag can be ahead of the independently deployed API flag.
  // A route-level 404 marks the bridge unavailable for this page lifetime and
  // restores the existing Auth0 paths without requiring another dashboard build.
  const [isBetterAuthAvailable, setIsBetterAuthAvailable] = useState(betterAuthEnabled)
  // A flag-on boot starts unresolved until the build-selected transport has
  // checked storage. This prevents either the login page or a lower-priority
  // Auth0 principal from flashing before a stored Better Auth token is known.
  const [isBetterAuthLoading, setIsBetterAuthLoading] = useState(betterAuthEnabled)
  const refreshTokenPromiseRef = useRef<Promise<string> | null>(null)

  const auth0SessionUser = auth0IsAuthenticated ? auth0User : undefined
  // Precedence: Better Auth (when enabled) -> direct Auth0 tokens -> Auth0 SPA.
  // During rollout all three can be reachable; an established Better Auth
  // session wins so a stale Auth0 session can't shadow it.
  const user = isBetterAuthLoading
    ? undefined
    : (betterAuthSession?.user ?? directSession?.user ?? auth0SessionUser)
  const isAuthenticated =
    !isBetterAuthLoading && (!!betterAuthSession || !!directSession || auth0IsAuthenticated)
  const isAuth0Loading = betterAuthSession || directSession ? false : auth0Loading

  const clearDirectSession = useCallback(() => {
    setDirectSession(null)
    writeDirectSession(null)
  }, [])

  // Boot: validate the stored Better Auth token and hydrate the session behind
  // it. A transient failure is not an authentication verdict: keep the priority
  // barrier up and retry until the server definitively accepts or rejects it.
  useEffect(() => {
    if (!betterAuthEnabled) return

    let cancelled = false
    let retryTimer: number | undefined
    let retryDelayMs = BETTER_AUTH_BOOT_RETRY_INITIAL_MS

    const validateStoredSession = async () => {
      try {
        const token = getStoredBetterAuthToken()
        if (!token) {
          setIsBetterAuthLoading(false)
          return
        }

        const sessionUser = await fetchBetterAuthSession()
        if (cancelled) return
        if (sessionUser) {
          setBetterAuthSession({ token, user: betterAuthUserToAuthUser(sessionUser) })
        } else {
          // Definitive "no session" — the token is dead, stop sending it.
          clearBetterAuthToken()
        }
        setIsBetterAuthLoading(false)
      } catch (error) {
        if (cancelled) return
        if (isBetterAuthUnavailableError(error)) {
          setIsBetterAuthAvailable(false)
          setIsBetterAuthLoading(false)
          return
        }
        retryTimer = window.setTimeout(() => {
          void validateStoredSession()
        }, retryDelayMs)
        retryDelayMs = Math.min(retryDelayMs * 2, BETTER_AUTH_BOOT_RETRY_MAX_MS)
      }
    }

    void validateStoredSession()

    return () => {
      cancelled = true
      if (retryTimer !== undefined) window.clearTimeout(retryTimer)
    }
  }, [])

  const refreshDirectAccessToken = useCallback(async () => {
    if (!directSession) {
      throw new Error('Session expired. Please log in again.')
    }
    if (refreshTokenPromiseRef.current) {
      return await refreshTokenPromiseRef.current
    }

    // Inside the mobile WebView shell the native side owns refresh — ask it
    // for a fresh session instead of hitting Auth0 from the WebView (would
    // race the native side's refresh and burn the rotating refresh token).
    if (isMobileBridge()) {
      const bridgePromise = (async () => {
        const bridgeSession = await requestMobileBridgeRefresh()
        const nextSession: DirectAuthSession = {
          accessToken: bridgeSession.accessToken,
          idToken: bridgeSession.idToken,
          refreshToken: bridgeSession.refreshToken,
          expiresAt: bridgeSession.expiresAt,
          user: bridgeSession.user,
        }
        setDirectSession(nextSession)
        writeDirectSession(nextSession)
        return nextSession.accessToken
      })()
      refreshTokenPromiseRef.current = bridgePromise.finally(() => {
        refreshTokenPromiseRef.current = null
      })
      return await refreshTokenPromiseRef.current
    }

    if (!directSession.refreshToken) {
      throw new Error('Session expired. Please log in again.')
    }

    const domain = import.meta.env.VITE_AUTH0_DOMAIN as string | undefined
    const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID as string | undefined
    if (!domain || !clientId) {
      throw new Error('Auth is not configured')
    }

    const refreshPromise = (async () => {
      const response = await fetch(`https://${domain}/oauth/token`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
        },
        body: JSON.stringify({
          grant_type: 'refresh_token',
          client_id: clientId,
          refresh_token: directSession.refreshToken,
          doneverse_origin: window.location.origin,
        }),
      })

      const json = (await response.json().catch(() => ({}))) as {
        access_token?: string
        id_token?: string
        refresh_token?: string
        expires_in?: number
      }

      if (!response.ok || !json.access_token) {
        throw createStatusError(
          authErrorMessage(json, 'Session expired. Please log in again.'),
          response.status,
        )
      }

      const nextIdToken = json.id_token || directSession.idToken
      const nextUser = nextIdToken ? buildUserFromIdToken(nextIdToken) : directSession.user
      const nextSession: DirectAuthSession = {
        accessToken: json.access_token,
        idToken: nextIdToken,
        refreshToken: json.refresh_token || directSession.refreshToken,
        expiresAt: Date.now() + Math.max((json.expires_in ?? 3600) * 1000, 60_000),
        user: nextUser,
      }

      setDirectSession(nextSession)
      writeDirectSession(nextSession)
      return nextSession.accessToken
    })()

    refreshTokenPromiseRef.current = refreshPromise.finally(() => {
      refreshTokenPromiseRef.current = null
    })

    return await refreshTokenPromiseRef.current
  }, [directSession])

  const getAccessToken = useCallback(async () => {
    if (!isAuthenticated) {
      throw new Error('Not authenticated')
    }
    if (betterAuthEnabled && betterAuthSession) {
      // Nothing to refresh: the token is opaque and stable, and the server
      // extends the session's expiry as it's used. Read storage rather than
      // state so a token the Better Auth client rotated (`set-auth-token`) is
      // picked up without waiting for a re-render.
      return getStoredBetterAuthToken() ?? betterAuthSession.token
    }
    if (directSession) {
      if (Date.now() < directSession.expiresAt - TOKEN_REFRESH_BUFFER_MS) {
        return directSession.accessToken
      }
      try {
        return await refreshDirectAccessToken()
      } catch (error) {
        if (isAuthHttpError(error)) {
          clearDirectSession()
        }
        throw error instanceof Error ? error : new Error('Session expired. Please log in again.')
      }
    }
    return await getAccessTokenSilently({
      authorizationParams: {
        audience: import.meta.env.VITE_AUTH0_AUDIENCE,
        scope: 'openid profile email offline_access',
      },
    })
  }, [
    betterAuthSession,
    clearDirectSession,
    directSession,
    getAccessTokenSilently,
    isAuthenticated,
    refreshDirectAccessToken,
  ])

  // Supabase realtime authenticates with the Auth0 ID token (third-party auth
  // provider). We refresh first to keep it fresh, then read the raw JWT.
  const getRealtimeJwt = useCallback(async () => {
    if (!isAuthenticated) throw new Error('Not authenticated')

    if (betterAuthEnabled && betterAuthSession) {
      // The API's realtime verifier accepts a Better Auth session token
      // directly, so there is no separate JWT to hand out on this path.
      return await getAccessToken()
    }

    if (directSession) {
      // getAccessToken() refreshes when stale and persists the new session to
      // localStorage synchronously before resolving, so reading from there
      // beats reading React state (which hasn't re-rendered yet).
      await getAccessToken()
      const idToken = readDirectSession()?.idToken
      if (!idToken) throw new Error('Missing Auth0 ID token')
      return idToken
    }

    await getAccessTokenSilently({
      authorizationParams: {
        audience: import.meta.env.VITE_AUTH0_AUDIENCE,
        scope: 'openid profile email offline_access',
      },
    })
    const idToken = (await getIdTokenClaims())?.__raw
    if (!idToken) throw new Error('Missing Auth0 ID token')
    return idToken
  }, [
    betterAuthSession,
    directSession,
    getAccessToken,
    getAccessTokenSilently,
    getIdTokenClaims,
    isAuthenticated,
  ])

  useEffect(() => {
    if (isAuthenticated) {
      setAccessTokenGetter(getAccessToken)
    } else {
      setAccessTokenGetter(null)
    }

    return () => {
      setAccessTokenGetter(null)
    }
  }, [getAccessToken, isAuthenticated])

  // Inside the mobile WebView shell, the native side may push token updates
  // outside of a refresh-request (e.g. after its own proactive refresh).
  // Install a handler that mirrors those pushes into our React state and
  // localStorage so the rest of the app sees the latest tokens.
  useEffect(() => {
    if (!isMobileBridge()) return
    setMobileBridgeAcceptHandler((session) => {
      const next: DirectAuthSession = {
        accessToken: session.accessToken,
        idToken: session.idToken,
        refreshToken: session.refreshToken,
        expiresAt: session.expiresAt,
        user: session.user,
      }
      setDirectSession(next)
      writeDirectSession(next)
    })
    return () => setMobileBridgeAcceptHandler(null)
  }, [])

  const impersonatedUserId = getImpersonatedUser()?.id ?? null
  const profileQueryUserKey = impersonatedUserId ?? user?.id ?? null

  const {
    data: rawProfile = null,
    isLoading: isProfileLoading,
    error: profileError,
  } = useQuery({
    ...userProfileOptions(profileQueryUserKey),
    queryFn: async () => {
      await getAccessToken()
      const fetched = await profileService.fetchProfile()
      // Establish the active workspace before this query resolves: the
      // AuthBootstrapBoundary holds the router (and its workspace-scoped
      // prefetches) until `loading` flips false, so doing it here guarantees
      // the first request already carries the workspace header.
      bootstrapWorkspaceSelection(queryClient, fetched.id, fetched.workspaces)
      return fetched
    },
    enabled: !!(isAuthenticated && user),
  })

  const profile = isAuthenticated ? rawProfile : null
  const activeUserId = profile?.id ?? null

  // Tell the store who the effective user is so the API client can resolve the
  // per-user workspace selection for the `x-doneverse-workspace-id` header.
  useEffect(() => {
    useDashboardStore.getState().setCurrentUserId(activeUserId)
  }, [activeUserId])

  // Error identity must track the session for its whole life, not just its
  // happy start. Sentry's scope is global and sticky, so every branch has to
  // write it — including the ones that used to fall through and silently keep
  // whoever was here before. `profile` goes null on passive session loss too
  // (expired token, a 401 dropping the profile query), not only on logout.
  useEffect(() => {
    if (!profile) {
      clearErrorUser()
      return
    }

    if (impersonatedUserId) {
      // Attributed to nobody, tagged as impersonation — see lib/error-user.ts
      // for why neither the admin nor the impersonated user is the right
      // answer. PostHog is left alone here for the same reason it always was:
      // an admin's session must not land in the impersonated user's analytics.
      setImpersonatedErrorSession()
      return
    }

    identifyUser(profile.id, {
      userId: profile.id,
      email: profile.email,
      role: profile.role,
      founder_category: profile.founder_category?.code ?? null,
    })
    // Same id PostHog identifies on, so an error can be followed to the
    // session that produced it. Id and role only — see lib/error-user.ts.
    setErrorUser({ id: profile.id, role: profile.role })
    // `impersonatedUserId` is a dependency, not just a read: on stopping
    // impersonation React Query can hand back the byte-identical cached
    // profile, and keying only on `profile` would leave the session tagged as
    // impersonated for as long as it lives.
  }, [profile, impersonatedUserId])

  const updateBusinessBriefInternal = async (businessBrief: BusinessBriefData) => {
    await profileService.updateBusinessBrief(businessBrief)
  }

  const loginWithPassword = useCallback(
    async (email: string, password: string): Promise<LoginResult> => {
      const trimmedEmail = email.trim().toLowerCase()
      if (!trimmedEmail || !password) {
        return { error: new Error('Email and password are required') }
      }

      // Better Auth bridge: the same embedded form, but the API verifies the
      // password against Auth0 and mints a Better Auth session — the browser
      // never holds Auth0 tokens, so none of the VITE_AUTH0_* config below is
      // needed on this path.
      if (betterAuthEnabled && isBetterAuthAvailable) {
        let token: string | null = null
        try {
          token = await loginWithBetterAuthPassword(trimmedEmail, password)
          const sessionUser = await fetchBetterAuthSession()
          if (!sessionUser) {
            throw new Error('Login failed. Please try again.')
          }
          await queryClient.invalidateQueries({ queryKey: queryKeys.profile })
          setBetterAuthSession({ token, user: betterAuthUserToAuthUser(sessionUser) })
          setIsBetterAuthLoading(false)
          return { error: null }
        } catch (error) {
          // Once sign-in has minted/persisted a token, returning an error without
          // reconciling it would make reload authenticate a login the form called
          // failed. Tear it down before allowing another submission.
          if (token) {
            await logoutBetterAuth()
            setBetterAuthSession(null)
          }
          if (isBetterAuthUnavailableError(error)) {
            // The API flag was disabled before this dashboard build. Restore
            // the legacy direct Auth0 password path for this page lifetime.
            setIsBetterAuthAvailable(false)
            setIsBetterAuthLoading(false)
          } else {
            return {
              error: error instanceof Error ? error : new Error('Login failed. Please try again.'),
            }
          }
        }
      }

      const domain = import.meta.env.VITE_AUTH0_DOMAIN as string | undefined
      const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID as string | undefined
      const audience = import.meta.env.VITE_AUTH0_AUDIENCE as string | undefined
      const connection =
        (import.meta.env.VITE_AUTH0_CONNECTION as string | undefined) || 'Username-Password-Authentication'

      if (!domain || !clientId || !audience) {
        return { error: new Error('Auth is not configured') }
      }

      try {
        const response = await fetch(`https://${domain}/oauth/token`, {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            grant_type: 'http://auth0.com/oauth/grant-type/password-realm',
            client_id: clientId,
            audience,
            scope: 'openid profile email offline_access',
            username: trimmedEmail,
            password,
            realm: connection,
            doneverse_origin: window.location.origin,
          }),
        })

        const json = (await response.json().catch(() => ({}))) as {
          access_token?: string
          id_token?: string
          refresh_token?: string
          expires_in?: number
        }

        if (!response.ok || !json.access_token || !json.id_token) {
          return { error: new Error(authErrorMessage(json)) }
        }

        const nextSession: DirectAuthSession = {
          accessToken: json.access_token,
          idToken: json.id_token,
          refreshToken: json.refresh_token,
          expiresAt: Date.now() + Math.max((json.expires_in ?? 3600) * 1000, 60_000),
          user: buildUserFromIdToken(json.id_token),
        }

        setDirectSession(nextSession)
        writeDirectSession(nextSession)
        await queryClient.invalidateQueries({ queryKey: queryKeys.profile })
        return { error: null }
      } catch (error) {
        return {
          error: error instanceof Error ? error : new Error('Login failed. Please try again.'),
        }
      }
    },
    [isBetterAuthAvailable, queryClient],
  )

  // Sign-up always goes to Auth0: it stays the identity store and the owner of
  // provisioning (the Post-Login Action / verify-invite creates the `users`
  // row). Better Auth only takes over the *session*, which the trailing
  // loginWithPassword establishes when the bridge is on.
  const signupWithPassword = useCallback(
    async (email: string, password: string): Promise<LoginResult> => {
      const trimmedEmail = email.trim().toLowerCase()
      if (!trimmedEmail || !password) {
        return { error: new Error('Email and password are required') }
      }

      if (password.length < 8) {
        return { error: new Error('Password must be at least 8 characters long') }
      }

      const domain = import.meta.env.VITE_AUTH0_DOMAIN as string | undefined
      const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID as string | undefined
      const connection =
        (import.meta.env.VITE_AUTH0_CONNECTION as string | undefined) || 'Username-Password-Authentication'

      if (!domain || !clientId) {
        return { error: new Error('Auth is not configured') }
      }

      try {
        const response = await fetch(`https://${domain}/dbconnections/signup`, {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            client_id: clientId,
            email: trimmedEmail,
            password,
            connection,
          }),
        })

        const json = (await response.json().catch(() => ({}))) as Record<string, unknown>

        if (!response.ok) {
          return { error: new Error(authErrorMessage(json, 'Signup failed. Please try again.')) }
        }

        return await loginWithPassword(trimmedEmail, password)
      } catch (error) {
        return {
          error: error instanceof Error ? error : new Error('Signup failed. Please try again.'),
        }
      }
    },
    [loginWithPassword],
  )

  const directSessionRef = useRef(directSession)
  directSessionRef.current = directSession
  const betterAuthSessionRef = useRef(betterAuthSession)
  betterAuthSessionRef.current = betterAuthSession

  const logout = useCallback(
    async (options?: LogoutOptions) => {
      const returnTo = resolveLogoutReturnTo(options?.returnTo)
      setIsLoggingOut(true)
      setImpersonatedUser(null)
      queryClient.clear()
      resetUser()
      clearErrorUser()

      try {
        const hadBetterAuthSession = !!betterAuthSessionRef.current
        const hadDirectSession = !!directSessionRef.current
        const shouldEndAuth0Remotely = auth0IsAuthenticated || auth0Loading

        // Remove every lower-priority local fallback before waiting on the
        // Better Auth revoke request. A slow revoke must not leave another
        // principal ready to take over if the page reloads mid-logout.
        setBetterAuthSession(null)
        clearDirectSession()
        // `openUrl: false` only clears the SDK cache; it performs no remote
        // navigation. Continue teardown even if the SDK reports a local error.
        try {
          await auth0Logout({ openUrl: false })
        } catch {
          // Continue clearing the other session systems.
        }

        if (betterAuthEnabled) {
          // Drop the bridge token even when another principal owns this session:
          // a token left in storage would silently re-authenticate the user on
          // the next reload. Revoke server-side only when the Better Auth
          // session is the one being ended.
          if (hadBetterAuthSession) {
            await logoutBetterAuth() // revokes + clears locally; never throws
          } else {
            clearBetterAuthToken()
          }
        }

        if (isMobileBridge()) {
          // The native shell owns its own direct session and navigation.
          if (hadDirectSession) postMobileBridgeSignOut()
          return
        }

        if (shouldEndAuth0Remotely) {
          // A coexisting (or still-hydrating) Auth0 session must be ended too;
          // its logout redirect is the final navigation.
          await auth0Logout({ logoutParams: { returnTo } })
          return
        }

        // The local Auth0 cache was cleared above; no remote session is known.
        window.location.href = returnTo
      } catch (error) {
        setIsLoggingOut(false)
        throw error
      }
    },
    [auth0IsAuthenticated, auth0Loading, auth0Logout, clearDirectSession, queryClient],
  )

  const loginWithRedirect = async (options?: RedirectLoginOptions) => {
    await auth0LoginWithRedirect({
      ...options,
      authorizationParams: {
        audience: import.meta.env.VITE_AUTH0_AUDIENCE,
        redirect_uri: `${window.location.origin}/callback`,
        scope: 'openid profile email offline_access',
        ...options?.authorizationParams,
      },
    })
  }

  const updateProfile = async (
    updates: Partial<Omit<Profile, 'business_brief'>> & {
      business_brief?: BusinessBriefData
    },
  ) => {
    if (!isAuthenticated) return { error: new Error('Not authenticated') }

    try {
      if (updates.business_brief !== undefined) {
        await updateBusinessBriefInternal(updates.business_brief)
      }

      queryClient.setQueryData(
        userProfileOptions(profileQueryUserKey).queryKey,
        (prev: Profile | undefined) => {
          if (!prev) return prev
          const next = { ...prev, ...updates } as Profile
          if (updates.business_brief !== undefined) {
            next.business_brief = updates.business_brief ?? null
          }
          return next
        },
      )

      return { error: null }
    } catch (error) {
      console.error('Error updating profile:', error)
      return {
        error: error instanceof Error ? error : new Error('Failed to update profile'),
      }
    }
  }

  const isLoadingCombined = isBetterAuthLoading || isAuth0Loading || isProfileLoading
  const userWithId: AuthUser | undefined = user
    ? {
        ...user,
        id: profile?.id ?? ('id' in user ? (user.id as string) : undefined) ?? user.sub ?? '',
      }
    : undefined
  const authError =
    isAuthenticated && profileError
      ? profileError instanceof Error
        ? profileError.message
        : 'Failed to load profile'
      : null
  const authErrorStatus = isAuthenticated ? getErrorStatus(profileError) : null

  return (
    <AuthContext.Provider
      value={{
        user: userWithId,
        isAuthenticated,
        isLoading: isLoadingCombined,
        loading: isLoadingCombined,
        isLoggingOut,
        profile,
        authError,
        authErrorStatus,
        getAccessToken,
        getRealtimeJwt,
        loginWithPassword,
        signupWithPassword,
        loginWithRedirect,
        logout,
        updateProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
